import React from 'react';

import {assert} from 'Asserts';
import {
  LeafType,
  BenchlingTypeDef,
  isBenchlingTypeDefComplex,
  isBenchlingTypeDefList,
  isBenchlingTypeLeafType,
} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {ComplexTypeOperator} from 'data-types/benchling-type-def/ComplexTypes';
import isValidBenchlingTypeValue from 'data-types/util/isValidBenchlingTypeValue';
import {JSONValue} from 'util/Types';

import {LEAF_TYPE_TO_VALUE_INPUT as LEAF_TYPE_TO_VALUE_INPUT_INTERNAL} from './internals/BenchlingTypeValueInputConstants';
import {isMultiSupportedType, ValueInputComponentType} from './internals/ValueInputTypes';

export const LEAF_TYPE_TO_VALUE_INPUT = LEAF_TYPE_TO_VALUE_INPUT_INTERNAL;

// Turn off auto focus on inputs with dropdown menus so the menu doesn't appear when initially rendering,
// which gives a nicer UX by not blocking other visual elements that may be above or below.
const AUTO_FOCUS_SUPPORTED_LEAF_TYPE_INPUTS: Array<LeafType> = [
  LeafType.DECIMAL,
  LeafType.INTEGER,
  LeafType.TEXT,
];

export interface BenchlingTypeValueInputProps {
  allowAutoFocus: boolean;
  schemaFieldSelectorId?: string;
  schemaId?: string;
  typeDef: BenchlingTypeDef;
  onChange: (newValue: JSONValue, isValid: boolean) => void;
  value: JSONValue;
  disabled?: boolean;
}

/**
 * Input component that renders the appropriate value input for a given `BenchlingTypeDef`. For example,
 * a typeDef of `LeafType.BIOENTITY` and its subtypes will render a `BioentitySearchInputWithItems` that is
 * filtered down to the appropriate bioentity type and/or `schemaId`.
 *
 * Currently not all benchling types are supported. Supported leaf types are those with an input component
 * listed in `LEAF_TYPE_TO_VALUE_INPUT`. Supported complex types are currently include `List` and `Nullable`.
 * Compositions of complex types (e.g. `List(Nullable(leafType))`) are not yet supported.
 */
export default function BenchlingTypeValueInput({
  allowAutoFocus,
  schemaFieldSelectorId,
  schemaId,
  typeDef,
  onChange,
  value,
  disabled,
}: BenchlingTypeValueInputProps): JSX.Element {
  const baseType = isBenchlingTypeDefComplex(typeDef) ? typeDef.getBaseTypes()[0] : typeDef;

  assert(!isBenchlingTypeDefComplex(baseType), 'Compound complex types are not supported');
  assert(isBenchlingTypeLeafType(baseType), 'Only supports base types that are LeafTypes');

  if (isBenchlingTypeDefComplex(typeDef)) {
    assert(typeDef.getOperatorName() !== ComplexTypeOperator.UNION, 'Union types are not supported');
    assert(
      !isBenchlingTypeDefList(typeDef) || isMultiSupportedType(baseType),
      `List type defs are not supported for ${baseType}`
    );
  }

  const leafType = baseType;
  const ValueInputComponent: ValueInputComponentType<LeafType> | null = LEAF_TYPE_TO_VALUE_INPUT[leafType];

  assert(ValueInputComponent != null, 'Type is not yet supported', {baseType});

  const isValueValid = isValidBenchlingTypeValue(value, typeDef);

  function onChangeWithValidityCheck(newValue: JSONValue): void {
    onChange(newValue, isValidBenchlingTypeValue(newValue, typeDef));
  }

  return (
    <ValueInputComponent
      allowAutoFocus={allowAutoFocus && AUTO_FOCUS_SUPPORTED_LEAF_TYPE_INPUTS.includes(baseType)}
      schemaFieldSelectorId={schemaFieldSelectorId}
      schemaId={schemaId}
      typeDef={typeDef}
      valueInput={{value, isValid: isValueValid}}
      onChange={onChangeWithValidityCheck}
      disabled={disabled}
    />
  );
}
